import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, RectangleVertical, RectangleHorizontal, ScanLine, Boxes, Cpu } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Reveal from "../components/Reveal";
import { InteriorPortrait, InteriorLandscape, InteriorAerial, ExplodedAssembly, PartGlyph } from "../components/shop/InteriorDrawings";
import { PARTS, INTERIOR_NOTES } from "../data/interior";
import { GRP_META } from "../data/build";

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/shop/interior", label: "Interior" },
  { to: "/shop/brief", label: "Brief / RFQ" },
  { to: "/software", label: "Software" },
  { to: "/home", label: "Home System" },
];

function Head({ icon: Icon, eyebrow, title, sub }) {
  return (
    <Reveal>
      <div className="max-w-3xl">
        <p className="eyebrow mb-3 flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {eyebrow}</p>
        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
        {sub && <p className="mt-3 text-slate-600 leading-relaxed">{sub}</p>}
      </div>
    </Reveal>
  );
}

const DrawingCard = ({ children }) => (
  <Reveal delay={0.08}>
    <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4 md:p-8 shadow-[0_40px_80px_-55px_rgba(3,105,161,0.4)]">{children}</div>
  </Reveal>
);

export default function ShopInterior() {
  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="shop-interior-page">
      <TopBar links={LINKS} />

      {/* Hero */}
      <section className="relative pt-14 pb-10 md:pt-20 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50/70 to-white" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-5">
              <Layers className="h-3.5 w-3.5" /> Interior pack · Project 1
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Inside the tester, <span className="aqua-text">to scale</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Cover-off interior layout in three dimensioned views, a full 3D exploded assembly, and an individual
              drawing for every component. Hand-drawn to scale — no AI guesswork on where the plumbing goes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portrait */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={RectangleVertical} eyebrow="View 1 · dimensioned" title="Portrait — front internal elevation" sub="Looking at the front with the shell and cover removed. Wet measurement zone at left, dry electronics at right." />
          <DrawingCard><div data-testid="drawing-portrait"><InteriorPortrait /></div></DrawingCard>
        </div>
      </section>

      {/* Landscape */}
      <section className="py-12 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={RectangleHorizontal} eyebrow="View 2 · dimensioned" title="Landscape — side internal section" sub="Front-to-rear section: screen at the front, sample tank in the middle, PCB stack and battery at the rear behind the service cover." />
          <DrawingCard><div data-testid="drawing-landscape"><InteriorLandscape /></div></DrawingCard>
        </div>
      </section>

      {/* Aerial */}
      <section className="py-12 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={ScanLine} eyebrow="View 3 · dimensioned" title="Aerial — top-down internal plan" sub="Straight-down plan. The catch basin sits at the rear; the three labelled 6 mm strip slots (with their prompt LEDs) sit on the accessible front edge." />
          <DrawingCard><div data-testid="drawing-aerial"><InteriorAerial /></div></DrawingCard>
        </div>
      </section>

      {/* Exploded */}
      <section className="py-12 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Boxes} eyebrow="3D · pulled apart" title="Exploded assembly" sub="Every subassembly separated along the vertical build axis, numbered to the catalog below." />
          <DrawingCard><div className="max-w-3xl mx-auto" data-testid="drawing-exploded"><ExplodedAssembly /></div></DrawingCard>
        </div>
      </section>

      {/* Per-component catalog */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Head icon={Cpu} eyebrow="Every part, individually" title="Component catalog" sub="One card per component with its proposed size, material, quantity and function." />
          {/* legend */}
          <div className="mt-5 flex flex-wrap gap-3">
            {Object.entries(GRP_META).map(([g, m]) => (
              <span key={g} className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
                <span className="h-3 w-3 rounded-sm" style={{ background: m.color }} /> {m.label}
              </span>
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {PARTS.map((p) => (
              <Reveal key={p.n}>
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full hw-card" data-testid={`part-${p.n}`}>
                  <div className="relative bg-gradient-to-b from-slate-50 to-white p-4 grid-dots border-b border-slate-100">
                    <span className="absolute left-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-white font-mono2 text-[10px] font-bold">{p.n}</span>
                    {p.qty > 1 && <span className="absolute right-3 top-3 text-[10px] font-mono2 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">× {p.qty}</span>}
                    <PartGlyph shape={p.shape} grp={p.grp} />
                  </div>
                  <div className="p-4">
                    <p className="font-display font-bold text-slate-900 leading-snug">{p.name}</p>
                    <div className="mt-2 space-y-1">
                      <p className="font-mono2 text-[11px] text-sky-700">{p.size}</p>
                      <p className="font-mono2 text-[11px] text-slate-400">{p.material}</p>
                    </div>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed">{p.fn}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="interior-notes">
            <p className="font-display font-bold text-slate-900 text-sm mb-2">Engineering notes</p>
            <ul className="space-y-1.5">
              {INTERIOR_NOTES.map((t) => (
                <li key={t} className="text-sm text-slate-600 flex gap-2"><span className="text-amber-500">•</span> {t}</li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop/build" className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-sky-300 text-slate-800 px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="interior-to-build-btn">← Back to Build Spec</Link>
            <Link to="/software" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white px-6 py-3.5 rounded-full font-semibold transition-colors" data-testid="interior-to-software-btn">See the software it runs →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
